import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import usePopover from "components/common/usePopover";

/**
 * Month-grid date picker. Hand-rolled: phr has no date library and no UI kit,
 * and a native <input type="date"> can't be clamped to a provider's booking
 * window in a way the user can see (it just silently rejects).
 *
 * Also the progress-note task's due date (2026-10-05), with no window at all
 * (minDays/maxDays null). There the native picker's fault was different: on a
 * month change Chrome moves its highlight to the same day number, so "5 Nov"
 * looked chosen when nothing was. Here only the chosen day is ever filled, and
 * today is marked, not selected.
 *
 * All arithmetic is on LOCAL date parts. Never toISOString() here — that shifts
 * the day for anyone east/west of UTC, which would book the wrong date.
 */
const DAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** Local Y-M-D key — the only safe way to compare/emit a calendar day. */
export const toDateKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

const DatePicker = ({
  id, value, minDays, maxDays, disabled, invalid, menuClassName, onOpenChange, onChange,
}) => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const { triggerRef, menuRef, menuStyle } = usePopover(open, close);

  /* A dialog hosting this needs to know, so its own Escape can leave an open
     calendar to close itself (RescheduleModal's popoverOpenRef pattern). */
  useEffect(() => {
    if (onOpenChange) onOpenChange(open);
  }, [open, onOpenChange]);

  const today = startOfDay(new Date());
  /* null = no bound on that side (the task due date); numbers = the booking
     window, as before. */
  const min = useMemo(() => (minDays == null ? null : addDays(today, minDays)), [today, minDays]);
  const max = useMemo(() => (maxDays == null ? null : addDays(today, maxDays)), [today, maxDays]);

  const selected = value ? new Date(`${value}T00:00:00`) : null;
  /* The month it opens on: the chosen day's, else the first bookable one's,
     else today's. Re-applied on every open, as a native picker does — not left
     wherever the last browse stopped. */
  const homeMonth = () => {
    const base = selected || min || today;
    return new Date(base.getFullYear(), base.getMonth(), 1);
  };
  const [view, setView] = useState(homeMonth);

  const grid = useMemo(() => {
    const first = new Date(view.getFullYear(), view.getMonth(), 1);
    // Monday-first: JS getDay() is Sunday-first, so rotate.
    const lead = (first.getDay() + 6) % 7;
    const days = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < lead; i += 1) cells.push(null);
    for (let d = 1; d <= days; d += 1) {
      cells.push(new Date(view.getFullYear(), view.getMonth(), d));
    }
    return cells;
  }, [view]);

  const inRange = (d) => (!min || d >= min) && (!max || d <= max);
  const canPrev = !min || new Date(view.getFullYear(), view.getMonth(), 1) > min;
  const canNext = !max || new Date(view.getFullYear(), view.getMonth() + 1, 1) <= max;

  const label = selected
    ? `${selected.getDate()} ${MONTHS[selected.getMonth()].slice(0, 3)} ${selected.getFullYear()}`
    : "Select a date";

  return (
    <div className="zy-date">
      <button
        id={id}
        ref={triggerRef}
        type="button"
        className={`zy-select-trigger${invalid ? " is-invalid" : ""}${
          selected ? "" : " is-placeholder"
        }`}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          if (!open) setView(homeMonth());
          setOpen((o) => !o);
        }}
      >
        <span className="zy-select-value">{label}</span>
        <span className="zy-select-caret" aria-hidden="true">
          &#9662;
        </span>
      </button>

      {open &&
        ReactDOM.createPortal(
          <div
            className={`zy-date-menu${menuClassName ? ` ${menuClassName}` : ""}`}
            ref={menuRef}
            style={menuStyle}
            role="dialog"
          >
            <div className="zy-date-head">
              <button
                type="button"
                className="zy-date-nav"
                disabled={!canPrev}
                aria-label="Previous month"
                onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
              >
                &#8249;
              </button>
              <span className="zy-date-title">
                {MONTHS[view.getMonth()]} {view.getFullYear()}
              </span>
              <button
                type="button"
                className="zy-date-nav"
                disabled={!canNext}
                aria-label="Next month"
                onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
              >
                &#8250;
              </button>
            </div>

            <div className="zy-date-grid">
              {DAYS.map((d) => (
                <span className="zy-date-dow" key={d}>
                  {d}
                </span>
              ))}
              {grid.map((d, i) => {
                if (!d) return <span key={`pad-${i}`} className="zy-date-pad" />;
                const key = toDateKey(d);
                const ok = inRange(d);
                return (
                  <button
                    key={key}
                    type="button"
                    className={`zy-date-day${key === value ? " is-selected" : ""}${
                      key === toDateKey(today) ? " is-today" : ""
                    }`}
                    disabled={!ok}
                    onClick={() => {
                      setOpen(false);
                      onChange(key);
                    }}
                  >
                    {d.getDate()}
                  </button>
                );
              })}
            </div>

            {/* State the window rather than just greying days out — otherwise a
                doctor sees dead cells with no explanation. Only when there IS
                one: an open-ended picker has nothing to state. */}
            {min && max ? (
              <p className="zy-date-note">
                Bookable {minDays === 0 ? "from today" : `from ${minDays} day(s) ahead`} up
                to {maxDays} day(s) ahead.
              </p>
            ) : null}
          </div>,
          document.body
        )}
    </div>
  );
};

DatePicker.propTypes = {
  id: PropTypes.string,
  /** Local "YYYY-MM-DD", never an ISO instant. */
  value: PropTypes.string,
  /** From the provider row — 0 means today is bookable; null means no bound. */
  minDays: PropTypes.number,
  maxDays: PropTypes.number,
  disabled: PropTypes.bool,
  invalid: PropTypes.bool,
  /** Added to the portalled menu, e.g. to lift it above a modal. */
  menuClassName: PropTypes.string,
  /** Called with true/false as the calendar opens and closes. */
  onOpenChange: PropTypes.func,
  onChange: PropTypes.func.isRequired,
};

DatePicker.defaultProps = { minDays: 0, maxDays: 10 };

export default DatePicker;
